import { useContext } from 'react';
import type { ReactNode } from 'react';

import { ProductDetailsContext } from './context';

export type ProductAlias = {
  productAliasId: string;
  storeGuid?: string;
  aliasSKU: string;
  isNewAlias?: boolean;
  productId: number | null;
};

type DimensionsValue = {
  length: number | undefined;
  width: number | undefined;
  height: number | undefined;
};

export type ProductDangerousGoods = {
  // shared by DG and HAZMAT (two-way synced)
  idNumber?: string;
  shippingName?: string;
  packagingGroup?: 'i' | 'ii' | 'iii' | 'NoPackagingGroup';
  // DG-only
  technicalName?: string;
  productClass?: string;
  productClassSubsidiary?: string;
  packagingType?: string;
  quantity?: number;
  dangerousAmount?: { amount?: number; unit?: 'kg' | 'g' | 'l' | 'ml' };
  // HAZMAT-only
  naNumber?: string;
  ergReferenceNumber?: string;
  flashpointTemperatureF?: string;
  additionalDetails?: string;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  // section-enable meta
  dgSectionEnabled?: boolean;
  hazmatSectionEnabled?: boolean;
};

type ItemDimensionsValue = {
  length: number;
  width: number;
  height: number;
  unit: string | null;
};

type ItemWeightValue = {
  value: number;
  unit: string | null;
};

type ProductPriceValue = {
  value: number;
  code: string;
};

type HarmonizationWorkflow = {
  isActive: boolean;
  selectedCode: string | null;
  selectedConfidence: string | null;
};

type ValidationError = {
  message: string;
};

export type ProductDetailsContextValue = {
  productId: number | null;
  userHasPermission: boolean;
  closeModal: () => void;
  headerText: ReactNode;
  isActive: boolean;
  setIsActive: (isChecked: boolean) => void;
  isVariant: boolean;
  currentProductProfile: Record<string, unknown>;
  setCurrentProductProfile: (profile: Record<string, unknown>) => void;
  productProfiles: unknown[];
  productName: string;
  setProductName: (name: string) => void;
  productSku: string;
  setProductSku: (sku: string) => void;
  shouldOverrideImportedName: boolean;
  setShouldOverrideImportedName: (val: boolean) => void;
  productTypeText: unknown;
  weightValue: number | undefined;
  productItemWeight: ItemWeightValue;
  setProductItemWeight: (weight: ItemWeightValue) => void;
  setWeightValue: (value: number) => void;
  weightUnit: string | undefined;
  productWarehouseLocation: string;
  setProductWarehouseLocation: (val: string) => void;
  description: string;
  setDescription: (val: unknown) => void;
  dimensions: DimensionsValue;
  setDimensions: (dims: unknown) => void;
  productItemDimensions: ItemDimensionsValue;
  setProductItemDimensions: (dims: ItemDimensionsValue) => void;
  sizeUnit: string | undefined;
  productFulfillmentSku: string;
  setProductFulfillmentSku: (val: unknown) => void;
  domesticServiceId: string;
  setDomesticServiceId: (val: string) => void;
  domesticProviderId: string;
  setDomesticProviderId: (val: string) => void;
  domesticPackageTypeId: string;
  setDomesticPackageTypeId: (val: string) => void;
  packageSetId: string | null;
  setPackageSetId: (val: string | null) => void;
  isMultiPackageEnabled: boolean;
  setIsMultiPackageEnabled: (val: boolean) => void;
  domesticConfirmationId: string;
  setDomesticConfirmationId: (val: string) => void;
  domesticRateShopperGuid: string;
  setDomesticRateShopperGuid: (val: string) => void;
  orderTagIds: string[];
  setOrderTagIds: (val: string[]) => void;
  internationalServiceId: string;
  setInternationalServiceId: (val: string) => void;
  internationalProviderId: string;
  setInternationalProviderId: (val: string) => void;
  internationalPackageTypeId: string;
  setInternationalPackageTypeId: (val: string) => void;
  internationalConfirmationId: string;
  setInternationalConfirmationId: (val: string) => void;
  internationalRateShopperGuid: string;
  setInternationalRateShopperGuid: (val: string) => void;
  isReturnable: boolean;
  setIsReturnable: (val: boolean) => void;
  productCategoryId: string | undefined;
  setProductCategoryId: (val: string) => void;
  applyToOpenFulfillmentPlans: boolean;
  setApplyToOpenFulfillmentPlans: (val: boolean) => void;
  parentProduct: { tags?: string[] } & Record<string, unknown>;
  imageUrl: string;
  setImageUrl: (val: unknown) => void;
  onSaveClicked: (reuseNewProductModal?: boolean) => void;
  selectedTab: unknown;
  setSelectedTab: (val: unknown) => void;
  upc: string | undefined;
  setUpc: (val: unknown) => void;
  price: number | undefined;
  setPrice: (val: unknown) => void;
  cost: number | undefined;
  setCost: (val: unknown) => void;
  fulfillmentBarcode: string | undefined;
  setFulfillmentBarcode: (val: unknown) => void;
  modifiedDateUtc: string | undefined;
  createDateUtc: string | undefined;
  hasHadInventory: boolean;
  noAutoCustomsDeclaration: boolean;
  setNoAutoCustoms: (val: boolean) => void;
  customsDescription: string | undefined;
  setCustomsDescription: (val: unknown) => void;
  customsDeclaredValue: string | undefined;
  setCustomsDeclaredValue: (val: unknown) => void;
  customsHarmonizationCode: string | undefined;
  setCustomsHarmonizationCode: (code: string) => void;
  customsMIDCode: string;
  setCustomsMIDCode: (val: unknown) => void;
  nsManufacturerProductId: string;
  setNsManufacturerProductId: (val: string) => void;
  cpscCertificateVersionId: string;
  setCpscCertificateVersionId: (val: string) => void;
  cpscCertifierId: string;
  setCpscCertifierId: (val: string) => void;
  customsOriginCountry: string | undefined;
  setCustomsOriginCountry: (val: unknown) => void;
  activity: Record<string, unknown>;
  setActivity: (updater: (state: Record<string, unknown>) => Record<string, unknown>) => void;
  productAliases: ProductAlias[];
  setProductAliases: (aliases: ProductAlias[]) => void;
  inventoryWarehouses: unknown[];
  inventoryWarehouseLocations: unknown[];
  shouldTrackInventory: boolean;
  setShouldTrackInventory: (val: boolean) => void;
  reorderThreshold: string;
  setReorderThreshold: (val: string) => void;
  inventoryLocations: unknown[];
  isLoadingInventoryLocations: boolean;
  setShouldLoadInventoryLocations: (val: boolean) => void;
  productVariantIds: string[];
  setProductVariantIds: (val: string[]) => void;
  productType: string;
  setProductType: (val: string) => void;
  isSaveInPlaceRequired: boolean;
  setIsSaveInPlaceRequired: (val: boolean) => void;
  isSaveInPlaceLoading: boolean;
  setIsSaveInPlaceLoading: (val: boolean) => void;
  openDifferentProductModal: (productId: number) => void;
  autoSplitOption: string;
  setAutoSplitOption: (val: string) => void;
  autoSplitCustomQuantity: number;
  setAutoSplitCustomQuantity: (val: number) => void;
  productActiveLocations: unknown[];
  setProductActiveLocations: (val: unknown[]) => void;
  isSmartRoutingEnabled: boolean;
  bundlePackingSlipDisplayMode: string;
  productPrice: ProductPriceValue;
  setProductPrice: (val: unknown) => void;
  enableProductPrice: boolean;
  setEnableProductPrice: (val: boolean) => void;
  packagePreference: string | undefined;
  setPackagePreference: (val: string) => void;
  packagingRequirements: string | undefined;
  setPackagingRequirements: (val: string) => void;
  requireSerialNumber: boolean;
  setRequireSerialNumber: (val: boolean) => void;
  productDangerousGoods: ProductDangerousGoods | null;
  setProductDangerousGoods: (val: ProductDangerousGoods | null) => void;
  productMsdsFile: unknown | null;
  setProductMsdsFile: (val: unknown | null) => void;
  isShipStationFulfillmentEnabled: boolean;
  validationErrors: Record<string, ValidationError> | null | undefined;
  isFormValid: boolean;
  markFieldTouched: (fieldName: string) => void;
  touchedFields: string[];
  hasAttemptedSave: boolean;
  harmonizationWorkflow: HarmonizationWorkflow;
  setHarmonizationWorkflow: (val: HarmonizationWorkflow) => void;
};

export const useProductDetailsContext = (): ProductDetailsContextValue => {
  const context = useContext(ProductDetailsContext);
  if (!context) {
    throw new Error('useProductDetailsContext must be used within ProductDetailsContextProvider');
  }
  return context;
};
